import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { OfferLineTemplate, OfferTemplate, OfferTemplateLine } from "@/hooks/use-offers";

/**
 * Admin-hooks for produktkatalog: CRUD på offer_line_templates (priser,
 * navn, fase, default-flagg) og offer_templates + offer_template_lines
 * (size/addon-maler som forhåndsfyller nye tilbud).
 *
 * Kun tilgjengelig fra `/innstillinger/produktkatalog` (admin-rolle).
 * Endringer slår igjennom umiddelbart for nye tilbud — eksisterende
 * tilbud påvirkes ikke (offer_lines er kopier, ikke referanser).
 */

// ── offer_line_templates ───────────────────────────────────────

/** Inkluderer is_default_for_offers og default_quantity (admin-felt). */
export function useAdminOfferLineTemplates() {
  return useQuery({
    queryKey: ["admin-offer-line-templates"],
    queryFn: async (): Promise<OfferLineTemplate[]> => {
      const { data, error } = await supabase
        .from("offer_line_templates")
        .select("*")
        .order("phase")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as OfferLineTemplate[];
    },
  });
}

export function useUpsertOfferLineTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<OfferLineTemplate> & { id?: string }) => {
      const { id, ...rest } = input;
      if (id) {
        const { error } = await supabase
          .from("offer_line_templates")
          .update(rest)
          .eq("id", id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase
        .from("offer_line_templates")
        .insert(rest)
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "offer_line_templates");
    },
  });
}

export function useDeleteOfferLineTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("offer_line_templates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "offer_line_templates");
    },
  });
}

// ── offer_templates + offer_template_lines (size/addon-maler) ──

export function useAdminOfferTemplates() {
  return useQuery({
    queryKey: ["admin-offer-templates"],
    queryFn: async (): Promise<OfferTemplate[]> => {
      const { data, error } = await supabase
        .from("offer_templates")
        .select("*")
        .order("kind")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as OfferTemplate[];
    },
  });
}

export function useOfferTemplateLines(templateId: string | undefined) {
  return useQuery({
    queryKey: ["offer-template-lines", templateId],
    enabled: !!templateId,
    queryFn: async (): Promise<OfferTemplateLine[]> => {
      const { data, error } = await supabase
        .from("offer_template_lines")
        .select(`
          *,
          line_template:offer_line_templates(*)
        `)
        .eq("template_id", templateId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as OfferTemplateLine[];
    },
  });
}

export function useUpsertOfferTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<OfferTemplate> & { id?: string }) => {
      const { id, ...rest } = input;
      if (id) {
        const { error } = await supabase
          .from("offer_templates")
          .update(rest)
          .eq("id", id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase
        .from("offer_templates")
        .insert(rest)
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "offer_templates");
    },
  });
}

export function useUpsertOfferTemplateLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<OfferTemplateLine> & { id?: string; template_id: string },
    ) => {
      const { id, ...rest } = input;
      if (id) {
        const { error } = await supabase
          .from("offer_template_lines")
          .update(rest)
          .eq("id", id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase
        .from("offer_template_lines")
        .insert(rest)
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(qc, "offer_template_lines", {
        templateId: variables.template_id,
      });
    },
  });
}

export function useDeleteOfferTemplateLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; template_id: string }) => {
      const { error } = await supabase
        .from("offer_template_lines")
        .delete()
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(qc, "offer_template_lines", {
        templateId: variables.template_id,
      });
    },
  });
}
